<script>
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import OnboardingView from '../OnboardingView.vue';
import EmptyStateMessage from './EmptyStateMessage.vue';

// If isFetching stays true longer than this, show a retry button.
const STUCK_LOADING_TIMEOUT_MS = 10000;

export default {
  components: {
    OnboardingView,
    EmptyStateMessage,
  },
  props: {
    isOnExpandedLayout: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isAdmin } = useAdmin();

    const { accountScopedUrl } = useAccount();

    return {
      isAdmin,
      accountScopedUrl,
    };
  },
  data() {
    return {
      isLoadingStuck: false,
      stuckTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      allConversations: 'getAllConversations',
      inboxesList: 'inboxes/getInboxes',
      uiFlags: 'inboxes/getUIFlags',
      loadingChatList: 'getChatListLoadingStatus',
    }),
    isLoading() {
      return this.uiFlags.isFetching || this.loadingChatList;
    },
    loadingIndicatorMessage() {
      if (this.uiFlags.isFetching) {
        return this.$t('CONVERSATION.LOADING_INBOXES');
      }
      return this.$t('CONVERSATION.LOADING_CONVERSATIONS');
    },
    conversationMissingMessage() {
      if (!this.isOnExpandedLayout) {
        return this.$t('CONVERSATION.SELECT_A_CONVERSATION');
      }
      return this.$t('CONVERSATION.404');
    },
    newInboxURL() {
      return this.accountScopedUrl('settings/inboxes/new');
    },
    emptyClassName() {
      if (
        !this.inboxesList.length &&
        !this.uiFlags.isFetching &&
        !this.loadingChatList &&
        this.isAdmin
      ) {
        return 'h-full overflow-auto w-full';
      }
      return 'flex-1 min-w-0 px-0 flex flex-col items-center justify-center h-full bg-n-surface-1';
    },
  },
  watch: {
    isLoading(val) {
      if (val) {
        this.startStuckTimer();
      } else {
        this.clearStuckTimer();
      }
    },
  },
  mounted() {
    if (this.isLoading) this.startStuckTimer();
  },
  beforeUnmount() {
    this.clearStuckTimer();
  },
  methods: {
    startStuckTimer() {
      this.clearStuckTimer();
      this.isLoadingStuck = false;
      this.stuckTimer = setTimeout(() => {
        if (this.isLoading) this.isLoadingStuck = true;
      }, STUCK_LOADING_TIMEOUT_MS);
    },
    clearStuckTimer() {
      this.isLoadingStuck = false;
      if (this.stuckTimer) {
        clearTimeout(this.stuckTimer);
        this.stuckTimer = null;
      }
    },
    retryLoadInboxes() {
      this.clearStuckTimer();
      // bypassCache:true goes straight to the network, skipping IDB.
      // This breaks the stuck state even if openDB is permanently hung.
      this.$store.dispatch('inboxes/get', { bypassCache: true });
      this.startStuckTimer();
    },
  },
};
</script>

<template>
  <div :class="emptyClassName">
    <!-- Stuck loading guard: shown after 10s of unresolved isFetching -->
    <div
      v-if="isLoadingStuck"
      class="flex flex-col items-center justify-center gap-3 p-4"
    >
      <p class="text-sm text-n-slate-11">
        {{ $t('CONVERSATION.LOADING_INBOXES_STUCK') }}
      </p>
      <button
        class="px-4 py-2 text-sm rounded bg-woot-500 text-white hover:bg-woot-600"
        @click="retryLoadInboxes"
      >
        {{ $t('CONVERSATION.LOADING_RETRY') }}
      </button>
    </div>

    <woot-loading-state
      v-else-if="isLoading"
      :message="loadingIndicatorMessage"
    />
    <!-- No inboxes attached -->
    <div
      v-if="!inboxesList.length && !uiFlags.isFetching && !loadingChatList"
      class="clearfix mx-auto"
    >
      <OnboardingView v-if="isAdmin" />
      <EmptyStateMessage v-else :message="$t('CONVERSATION.NO_INBOX_AGENT')" />
    </div>
    <!-- Show empty state images if not loading -->

    <div
      v-else-if="!uiFlags.isFetching && !loadingChatList"
      class="flex flex-col items-center justify-center h-full"
    >
      <!-- No conversations available -->
      <EmptyStateMessage
        v-if="!allConversations.length"
        :message="$t('CONVERSATION.NO_MESSAGE_1')"
      />
      <EmptyStateMessage
        v-else-if="allConversations.length && !currentChat.id"
        :message="conversationMissingMessage"
      />
    </div>
  </div>
</template>
